import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AddWorkspaceSeatDialog } from "@/components/AddWorkspaceSeatDialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useWorkspaceAddons } from "@/hooks/useWorkspaceAddons";
import { tierEntitlements, tierWorkspaceAddOnMax, WORKSPACE_ADDON_PRICE } from "@/lib/tier";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Create-workspace flow:
 *  - Below the effective cap (base + already-purchased add-ons): show a
 *    name input and create immediately.
 *  - At the cap: if the tier allows more add-ons, show embedded checkout
 *    for the workspace_addon_monthly price. The webhook upserts a row
 *    in `workspace_addons`; the user returns to /checkout/return and can
 *    re-open this dialog to create after the seat appears.
 *  - If add-ons are exhausted/disallowed, show an upgrade prompt.
 */
export function NewWorkspaceDialog({ open, onOpenChange }: Props) {
  const { user } = useAuth();
  const { workspaces, refresh, setActive, workspaceLimit, canCreateWorkspace, activePersonaId } = useWorkspace();
  const { count: addonCount, refresh: refreshAddons } = useWorkspaceAddons();
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [seatDialogOpen, setSeatDialogOpen] = useState(false);

  // Refresh seat data whenever the dialog opens so we never show a stale cap.
  useEffect(() => {
    if (open) {
      refresh();
      refreshAddons();
    }
  }, [open, refresh, refreshAddons]);

  const primary = workspaces.find((w) => w.is_primary) ?? workspaces[0];
  const tier = primary?.account_tier ?? null;
  const ent = tierEntitlements(tier);
  const addonMax = tierWorkspaceAddOnMax(tier);
  const used = workspaces.length;
  const remaining = workspaceLimit === null ? null : Math.max(0, workspaceLimit - used);

  const addonsAllowed = ent.workspaces !== null && (addonMax === null || addonMax > 0);
  const purchasedAddons = ent.workspaces !== null && workspaceLimit !== null
    ? Math.max(0, workspaceLimit - ent.workspaces)
    : 0;
  const addonsRemaining = addonMax === null ? null : Math.max(0, addonMax - purchasedAddons);
  const canBuyMoreAddons = addonsAllowed && (addonsRemaining === null || addonsRemaining > 0);
  void addonCount;

  const reset = () => {
    setLabel("");
    setCreating(false);
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const create = async () => {
    if (!user) return;
    if (!activePersonaId) return toast.error("No active Persona — switch to a Persona first");
    const trimmed = label.trim();
    setCreating(true);
    const nextSort = workspaces.reduce((m, w) => Math.max(m, w.sort_order), 0) + 1;
    const { data, error } = await supabase
      .from("profiles")
      .insert({
        owner_user_id: user.id,
        // Workspace name and account display name are intentionally separate.
        // Leave both null when the user doesn't supply a name; the UI will
        // render "Workspace N" placeholders until they name it in Settings.
        display_name: trimmed || null,
        workspace_label: trimmed || null,
        is_primary: false,
        parent_profile_id: activePersonaId,
        account_tier: primary?.account_tier ?? "starter",
        sort_order: nextSort,
      })
      .select("id")
      .single();
    setCreating(false);
    if (error || !data) return toast.error(error?.message ?? "Could not create workspace");
    toast.success("Workspace created");
    await refresh();
    setActive(data.id);
    handleClose(false);
  };

  // -------- Render branches --------

  // 1. At cap — offer add-on or upgrade.
  if (!canCreateWorkspace) {
    return (
      <>
        <Dialog open={open} onOpenChange={handleClose}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>You've used all your workspace seats</DialogTitle>
              <DialogDescription>
                {workspaceLimit !== null && (
                  <>You're using {used} of {workspaceLimit} workspaces on the {tier} plan.{" "}</>
                )}
                {canBuyMoreAddons
                  ? `Add an extra workspace for $${WORKSPACE_ADDON_PRICE.monthly}/month.`
                  : "Upgrade your plan to unlock more workspaces."}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
              {canBuyMoreAddons ? (
                <Button onClick={() => { handleClose(false); setSeatDialogOpen(true); }}>
                  <Sparkles className="h-4 w-4" />
                  Add workspace seat
                </Button>
              ) : (
                <Button asChild>
                  <a href="/pricing">View plans</a>
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <AddWorkspaceSeatDialog open={seatDialogOpen} onOpenChange={setSeatDialogOpen} />
      </>
    );
  }

  // 3. Below cap — name + create.
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a new workspace</DialogTitle>
          <DialogDescription>
            {workspaceLimit === null
              ? "You can create unlimited workspaces on your current plan."
              : `${used} of ${workspaceLimit} workspaces used — ${remaining} ${remaining === 1 ? "seat" : "seats"} remaining.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="new-ws-label">Workspace name (optional)</Label>
          <Input
            id="new-ws-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Newsletter, Client work, Research"
            autoFocus
          />
          <p className="text-xs text-muted-foreground">
            You can leave this blank and name it later from Settings.
          </p>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => handleClose(false)} disabled={creating}>
            Cancel
          </Button>
          <Button onClick={create} disabled={creating}>
            {creating ? "Creating…" : "Create workspace"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
